/**
 * Mobile Ad Form Page
 * Mobile-optimized page for creating and editing advertisements
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AdForm, type AdFormData } from '../../components/advertising/AdForm';
import { createAd, updateAd, getMyAds } from '../../services/membersApi';
import type { Ad } from '../../types';

const MobileAdFormPage = () => {
  const { adId } = useParams<{ adId?: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAd, setIsLoadingAd] = useState(false);
  const [error, setError] = useState('');
  const [initialData, setInitialData] = useState<Ad | undefined>();

  const isEditing = !!adId;

  // Load existing ad data if editing
  useEffect(() => {
    const loadAd = async () => {
      if (!adId) return;

      setIsLoadingAd(true);
      setError('');

      try {
        const adsResult = await getMyAds();
        const ad = adsResult.data.find((a) => a.id === adId);

        if (ad) {
          setInitialData(ad);
        } else {
          setError('找不到該廣告');
        }
      } catch (err) {
        console.error('Failed to load ad:', err);
        setError('載入廣告失敗');
      } finally {
        setIsLoadingAd(false);
      }
    };

    loadAd();
  }, [adId]);

  const handleSubmit = async (data: AdFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const adData = {
        title: data.title,
        description: data.description,
        category: data.category,
        region: data.region,
        district: data.district,
        storeName: data.storeName,
        phone: data.phone,
        address: data.address,
        priceRange: data.priceRange,
        imageUrls: data.imageUrls,
        tags: data.tags,
      };

      if (isEditing && adId) {
        // Update existing ad
        const result = await updateAd(adId, adData);
        if (result) {
          navigate(`/ads/${adId}`);
        } else {
          setError('更新廣告失敗');
        }
      } else {
        // Create new ad
        const result = await createAd(adData);
        if (result) {
          // Navigate to member center instead of detail page
          // because new ads have 'pending' status and detail page won't show them
          navigate('/member');
        } else {
          setError('建立廣告失敗');
        }
      }
    } catch (err) {
      console.error('Failed to save ad:', err);
      setError(isEditing ? '更新廣告失敗' : '建立廣告失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (isEditing && adId) {
      navigate(`/ads/${adId}`);
    } else {
      navigate('/ads');
    }
  };

  if (isLoadingAd) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        載入中...
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem 0.5rem' }}>
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '1.25rem' }}>
          {isEditing ? '編輯廣告' : '建立新廣告'}
        </h1>
        <button
          onClick={handleCancel}
          style={{
            padding: '0.5rem 1rem',
            background: '#f0f0f0',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem',
          }}
        >
          返回
        </button>
      </div>

      {error && (
        <div style={{
          background: '#fee',
          color: '#c33',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid #fcc',
        }}>
          {error}
        </div>
      )}

      <AdForm
        initialData={initialData ? {
          title: initialData.title,
          description: initialData.description || undefined,
          category: initialData.category,
          region: initialData.region,
          district: initialData.district || undefined,
          storeName: initialData.storeName,
          phone: initialData.phone || undefined,
          address: initialData.address || undefined,
          priceRange: initialData.priceRange || undefined,
          imageUrls: initialData.imageUrls,
          tags: initialData.tags,
        } : undefined}
        onSubmit={handleSubmit}
        isEditing={isEditing}
        isLoading={isLoading}
      />
    </div>
  );
};

export default MobileAdFormPage;
